import API from "helpers/api";
import { Apis } from "services/api";
import { getAPIResponse } from "helpers/getApiResponse";

const getModelTaskAutocomplete = async (search, modelVersionId) => {
	try {
		let response = await API.get(
			`${Apis.ModelTasks}/autocomplete/${encodeURIComponent(
				search
			)}?modelVersionId=${encodeURIComponent(modelVersionId)}`
		);
		return getAPIResponse(response);
	} catch (error) {
		return getAPIResponse(error?.response);
	}
};

const getModelTaskPage = async ({
	modelVersionId,
	pageNumber = 1,
	pageSize = 50,
	textSearches = [],
	sortField = "pos",
	sortOrder = "asc",
	anchorTaskId,
	roleIds = [],
	intervalIds = [],
	zoneIds = [],
	stageIds = [],
	arrangementIds = [],
	signal,
}) => {
	try {
		const response = await API.get(`${Apis.ModelTasks}/paged`, {
			params: {
				modelVersionId,
				pageNumber,
				pageSize,
				textSearches,
				sortField,
				sortOrder,
				...(anchorTaskId ? { anchorTaskId } : {}),
				roleIds,
				intervalIds,
				zoneIds,
				stageIds,
				arrangementIds,
			},
			signal,
		});
		return getAPIResponse(response);
	} catch (error) {
		if (error?.code === "ERR_CANCELED") throw error;
		return getAPIResponse(error?.response);
	}
};

const getLengthOfModelTasks = async (modelVersionId) => {
	try {
		let response = await API.get(
			`${Apis.ModelTasks}/count?modelVersionId=${modelVersionId}`
		);
		return getAPIResponse(response);
	} catch (error) {
		return getAPIResponse(error?.response);
	}
};

const getSingleModelTask = async (modelVersionTaskId, signal) => {
	try {
		let response = await API.get(`${Apis.ModelTasks}/${modelVersionTaskId}`, {
			signal,
		});
		return getAPIResponse(response);
	} catch (error) {
		if (error?.code === "ERR_CANCELED") throw error;
		return getAPIResponse(error?.response);
	}
};

const addModelTask = async (payload) => {
	try {
		let response = await API.post(`${Apis.ModelTasks}`, payload);
		return getAPIResponse(response);
	} catch (error) {
		return getAPIResponse(error?.response);
	}
};

const duplicateTask = async (taskId) => {
	try {
		let response = await API.post(`${Apis.ModelTasks}/${taskId}/Duplicate`);
		return getAPIResponse(response);
	} catch (error) {
		return getAPIResponse(error?.response);
	}
};

const pasteModelTask = async (taskId, payload) => {
	try {
		let response = await API.post(
			`${Apis.ModelVersions}/${taskId}/PasteTask`,
			payload
		);
		return getAPIResponse(response);
	} catch (error) {
		return getAPIResponse(error?.response);
	}
};

const bulkDeleteModelTasks = async ({ modelVersionId, taskIds }) => {
	try {
		const response = await API.post(`${Apis.ModelTasks}/bulk-delete`, {
			modelVersionID: modelVersionId,
			taskIDs: taskIds,
		});
		return getAPIResponse(response);
	} catch (error) {
		return getAPIResponse(error?.response);
	}
};

const BULK_ASSIGN_RELATIONS = [
	"roles",
	"zones",
	"stages",
	"intervals",
	"arrangements",
];

const bulkAssignModelTaskRelation = async ({
	relation,
	modelVersionId,
	taskIds,
	addIds = [],
	removeIds = [],
}) => {
	try {
		const response = await API.post(
			`${Apis.ModelTasks}/bulk-assign-${relation}`,
			{
				modelVersionID: modelVersionId,
				taskIDs: taskIds,
				addIDs: addIds,
				removeIDs: removeIds,
			}
		);
		return getAPIResponse(response);
	} catch (error) {
		return getAPIResponse(error?.response);
	}
};

const bulkDuplicateModelTasks = async ({ modelVersionId, taskIds }) => {
	try {
		const response = await API.post(`${Apis.ModelTasks}/bulk-duplicate`, {
			modelVersionID: modelVersionId,
			taskIDs: taskIds,
		});
		return getAPIResponse(response);
	} catch (error) {
		return getAPIResponse(error?.response);
	}
};

const bulkPasteModelTaskQuestions = async ({
	modelVersionId,
	sourceTaskId,
	taskIds,
}) => {
	try {
		const response = await API.post(`${Apis.ModelTasks}/bulk-paste-questions`, {
			modelVersionID: modelVersionId,
			sourceTaskID: sourceTaskId,
			taskIDs: taskIds,
		});
		return getAPIResponse(response);
	} catch (error) {
		return getAPIResponse(error?.response);
	}
};

const bulkPasteModelTaskQuestion = async ({
	modelVersionId,
	sourceQuestionId,
	taskIds,
}) => {
	try {
		const response = await API.post(`${Apis.ModelTasks}/bulk-paste-question`, {
			modelVersionID: modelVersionId,
			sourceQuestionID: sourceQuestionId,
			taskIDs: taskIds,
		});
		return getAPIResponse(response);
	} catch (error) {
		return getAPIResponse(error?.response);
	}
};

const getModelTaskRelationSummary = async ({
	relation,
	modelVersionId,
	taskIds,
}) => {
	try {
		const response = await API.post(
			`${Apis.ModelTasks}/bulk-assign-${relation}/summary`,
			{ modelVersionID: modelVersionId, taskIDs: taskIds }
		);
		return getAPIResponse(response);
	} catch (error) {
		return getAPIResponse(error?.response);
	}
};

const patchModelTask = async (taskId, payload) => {
	try {
		let response = await API.patch(`${Apis.ModelTasks}/${taskId}`, payload);
		return getAPIResponse(response);
	} catch (error) {
		return getAPIResponse(error?.response);
	}
};

export {
	getModelTaskAutocomplete,
	addModelTask,
	duplicateTask,
	getSingleModelTask,
	getLengthOfModelTasks,
	pasteModelTask,
	patchModelTask,
	getModelTaskPage,
	bulkDeleteModelTasks,
	bulkDuplicateModelTasks,
	bulkPasteModelTaskQuestions,
	bulkPasteModelTaskQuestion,
	bulkAssignModelTaskRelation,
	getModelTaskRelationSummary,
	BULK_ASSIGN_RELATIONS,
};
